/**
 * Allocates the normalized typed-array representation shared by all
 * Gaussian scene loaders and renderers.
 */
export function createGaussianData(count, metadata = {}) {
  if (!Number.isInteger(count) || count < 0) {
    throw new Error(`Invalid Gaussian count: ${count}`);
  }

  return {
    ...metadata,
    count,
    positions: new Float32Array(count * 3),
    colors: new Float32Array(count * 3),
    opacities: new Float32Array(count),
    scales: new Float32Array(count * 3),
    rotations: new Float32Array(count * 4),
  };
}

/**
 * Returns the total memory occupied by the Gaussian attribute arrays.
 */
export function getGaussianDataByteLength(data) {
  if (!data) {
    return 0;
  }

  return (
    (data.positions?.byteLength ?? 0) +
    (data.colors?.byteLength ?? 0) +
    (data.opacities?.byteLength ?? 0) +
    (data.scales?.byteLength ?? 0) +
    (data.rotations?.byteLength ?? 0)
  );
}

/**
 * Validates that every Gaussian attribute array matches the declared
 * Gaussian count.
 */
export function validateGaussianData(data) {
  const count = data?.count;

  if (!Number.isInteger(count) || count < 0) {
    throw new Error("Gaussian data has an invalid count.");
  }

  const expectedLengths = {
    positions: count * 3,
    colors: count * 3,
    opacities: count,
    scales: count * 3,
    rotations: count * 4,
  };

  for (const [name, expectedLength] of Object.entries(expectedLengths)) {
    const actualLength = data[name]?.length ?? 0;

    if (actualLength !== expectedLength) {
      throw new Error(
        `Invalid Gaussian ${name}: expected ${expectedLength}, ` +
          `received ${actualLength}.`,
      );
    }
  }

  return true;
}
